import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUser } from 'store/users/usersType';
import { ICompanyRole } from 'store/companies/companiesTypes';
import { RootState, TThunkApi } from 'store/store';
import * as manApi from 'api/ManApi';
import { updateNestedObject } from '../objectHelpers';

export type LimitationType = 'edit' | 'changeData' | 'report' | 'read';

export interface ILimitation {
  id: number;
  categoryId: number | null;
  exceptedUsers: Array<IUser>;
  exceptedRoles: Array<ICompanyRole>;
  passcode: null;
}

export interface ILimitationState {
  id: number;
  categoryId: number | null;
  exceptedUsers: Array<number>;
  exceptedRoles: Array<number>;
  passcode: null;
}

export interface ILimitationStore {
  data: ILimitationState;
  isLoading: boolean;
  isValid: boolean;
}

export interface ILimitations {
  map: { [key: number]: ILimitationStore };
}

const initialState: ILimitations = {
  map: {},
};

const Limitation = (limitation: ILimitation) => {
  const { exceptedRoles, exceptedUsers } = limitation;

  // if (update from server we should use item.id , if we update from our local database we simply use item which is id
  const userIds = exceptedUsers.map((user) => user.id);
  const roleIds = exceptedRoles.map((role) => role.id);

  return {
    ...limitation,
    exceptedUsers: userIds,
    exceptedRoles: roleIds,
  };
};

function updateLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ limitation: ILimitation }>
) {
  state.map[action.payload.limitation.id] = {
    data: Limitation(action.payload.limitation),
    isLoading: false,
    isValid: true,
  };
}

function loadingLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ id: number }>
) {
  state = updateNestedObject<ILimitations>({
    obj: state,
    path: `state.map[${action.payload.id}]`,
    value: { isLoading: true },
  });
}

function failLoadingLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ id: number }>
) {
  state.map[action.payload.id] = {
    data: state.map[action.payload.id].data || {},
    isLoading: false,
    isValid: false,
  };
}

function addUserToLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ userId: number; limitationId: number }>
) {
  if (!state.map[action.payload.limitationId]) {
    // @ts-expect-error updating stp by step
    state.map = {
      ...state.map,
      [action.payload.limitationId]: { data: {} },
    };
  }
  if (!state.map[action.payload.limitationId].data.exceptedUsers) {
    state.map[action.payload.limitationId] = {
      ...state.map[action.payload.limitationId],
      data: {
        ...state.map[action.payload.limitationId].data,
        exceptedUsers: [],
      },
    };
  }

  state.map[action.payload.limitationId].data.exceptedUsers.push(
    action.payload.userId
  );
}

function removeUserFromLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ userId: number; limitationId: number }>
) {
  if (
    !state.map[action.payload.limitationId].data.exceptedUsers ||
    state.map[action.payload.limitationId].data.exceptedUsers.length === 0
  )
    return;

  state.map[action.payload.limitationId].data.exceptedUsers = state.map[
    action.payload.limitationId
  ].data.exceptedUsers.filter((itm) => itm !== action.payload.userId);
}

function addRoleToLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ roleId: number; limitationId: number }>
) {
  if (!state.map[action.payload.limitationId]) {
    // @ts-expect-error updating stp by step
    state.map = {
      ...state.map,
      [action.payload.limitationId]: { data: {} },
    };
  }
  if (!state.map[action.payload.limitationId].data.exceptedRoles) {
    state.map[action.payload.limitationId] = {
      ...state.map[action.payload.limitationId],
      data: {
        ...state.map[action.payload.limitationId].data,
        exceptedRoles: [],
      },
    };
  }

  state.map[action.payload.limitationId].data.exceptedRoles.push(
    action.payload.roleId
  );
}

function removeRoleFromLimitationHandler(
  state: ILimitations,
  action: PayloadAction<{ roleId: number; limitationId: number }>
) {
  if (
    !state.map[action.payload.limitationId].data.exceptedRoles ||
    state.map[action.payload.limitationId].data.exceptedRoles.length === 0
  )
    return;

  state.map[action.payload.limitationId].data.exceptedRoles = state.map[
    action.payload.limitationId
  ].data.exceptedRoles.filter((itm) => itm !== action.payload.roleId);
}

// Thunks
export const requireSingleLimitationFn = async (
  { id }: { id: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  const loading = store.limitations.map[id]?.isLoading;

  if (!loading) {
    ThunkApi.dispatch(loadingLimitation({ id }));

    return manApi
      .getLimitation(id, store)
      .then((res) => {
        ThunkApi.dispatch(updateLimitation({ limitation: res.data }));
        return Promise.resolve();
      })
      .catch((err) => {
        ThunkApi.dispatch(failLoadingLimitation({ id }));
        return ThunkApi.rejectWithValue(err) as any;
      });
  }
  return Promise.resolve();
};
const requireSingleLimitation = createAsyncThunk(
  'limitations/requireSingleLimitation',
  requireSingleLimitationFn
);

export const migrateLimitationsFn = async (
  {
    migrateList,
    companyId,
  }: {
    migrateList: Array<{
      userId: number;
      userName: string;
      roleId: number;
      roleName: string;
    }>;
    companyId: number;
  },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;

  return manApi
    .migrateLimitations(migrateList, companyId, store)
    .then(() => {
      return Promise.resolve();
    })
    .catch(() => {
      return Promise.reject();
    });
};

const migrateLimitations = createAsyncThunk(
  'limitations/migrateLimitations',
  migrateLimitationsFn
);
export const requireSeveralLimitationsFn = async (
  {
    limitList,
  }: {
    limitList: Array<number>;
  },
  ThunkApi: TThunkApi
) => {
  const promiseList = limitList.map((limitId) => {
    return ThunkApi.dispatch(requireSingleLimitation({ id: limitId }));
  });

  return Promise.allSettled(promiseList);
};
const requireSeveralLimitations = createAsyncThunk(
  'limitations/requireSeveralLimitations',
  requireSeveralLimitationsFn
);

export const addUserToLimitationFn = async (
  { limitationId, userId }: { limitationId: number; userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.permissionAddUser(limitationId, userId, store).then(
    () => {
      ThunkApi.dispatch(
        addUserToLimitationAction({
          limitationId: limitationId,
          userId: userId,
        })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addUserToLimitation = createAsyncThunk(
  'limitations/addUserToLimitation',
  addUserToLimitationFn
);

export const removeUserFromLimitationFn = async (
  { limitationId, userId }: { limitationId: number; userId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.permissionRemoveUser(limitationId, userId, store).then(
    () => {
      ThunkApi.dispatch(
        removeUserFromLimitationAction({ userId, limitationId })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const removeUserFromLimitation = createAsyncThunk(
  'limitations/removeUserFromLimitation',
  removeUserFromLimitationFn
);

export const addRoleToLimitationFn = async (
  { limitationId, roleId }: { limitationId: number; roleId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.permissionAddRole(limitationId, roleId, store).then(
    () => {
      ThunkApi.dispatch(
        addRoleToLimitationAction({
          roleId,
          limitationId,
        })
      );
      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const addRoleToLimitation = createAsyncThunk(
  'limitations/addRoleToLimitation',
  addRoleToLimitationFn
);

export const removeRoleFromLimitationFn = async (
  { limitationId, roleId }: { limitationId: number; roleId: number },
  ThunkApi: TThunkApi
) => {
  const store = ThunkApi.getState() as RootState;
  return manApi.permissionRemoveRole(limitationId, roleId, store).then(
    () => {
      ThunkApi.dispatch(
        removeRoleFromLimitationAction({
          roleId,
          limitationId,
        })
      );

      return Promise.resolve();
    },
    (err) => {
      return ThunkApi.rejectWithValue(err) as any;
    }
  );
};
const removeRoleFromLimitation = createAsyncThunk(
  'limitations/removeRoleFromLimitation',
  removeRoleFromLimitationFn
);

const limitationsSlice = createSlice({
  name: 'limitation',
  initialState: initialState,
  reducers: {
    updateLimitation: updateLimitationHandler,
    loadingLimitation: loadingLimitationHandler,
    failLoadingLimitation: failLoadingLimitationHandler,
    addUserToLimitationAction: addUserToLimitationHandler,
    removeUserFromLimitationAction: removeUserFromLimitationHandler,
    addRoleToLimitationAction: addRoleToLimitationHandler,
    removeRoleFromLimitationAction: removeRoleFromLimitationHandler,
  },
});

export const {
  updateLimitation,
  loadingLimitation,
  failLoadingLimitation,
  addUserToLimitationAction,
  removeUserFromLimitationAction,
  addRoleToLimitationAction,
  removeRoleFromLimitationAction,
} = limitationsSlice.actions;

export {
  requireSingleLimitation,
  migrateLimitations,
  requireSeveralLimitations,
  addUserToLimitation,
  removeUserFromLimitation,
  addRoleToLimitation,
  removeRoleFromLimitation,
};

export default limitationsSlice.reducer;
